import { CancelIcon } from '@/assets/icons'
import LocationAutocomplete, { PlaceSuggestion } from '@components/LocationAutocomplete'
import { EventCategory } from '@orbit/api-types'
import React from 'react'
import {
    AddLinkButton,
    FormContainer,
    FormGroup,
    Input,
    Label,
    LinkInput,
    LinkInputRow,
    LinksContainer,
    LocationSecondaryText,
    RemoveLinkButton,
    Select,
    TextArea,
} from './IdeaForm.styles'

const EVENT_CATEGORIES: EventCategory[] = [
	'FoodAndDrink',
	'SeeAndDo',
	'Nature',
	'Shopping',
	'Accommodation',
	'Transportation',
	'Event',
	'Other',
]

const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Events',
	Other: 'Other',
}

interface IdeaFormProps {
	formId: string
	name: string
	description: string
	category: EventCategory | undefined
	locationPrimaryText: string
	locationSecondaryText?: string
	links: string[]
	onNameChange: (value: string) => void
	onDescriptionChange: (value: string) => void
	onCategoryChange: (value: EventCategory | undefined) => void
	onLocationPrimaryTextChange: (value: string) => void
	onLocationSelect: (place: PlaceSuggestion) => void
	onLinksChange: (value: string[]) => void
	onSubmit: (e: React.FormEvent) => void
	attachmentSlot?: React.ReactNode
}

const IdeaForm: React.FC<IdeaFormProps> = ({
	formId,
	name,
	description,
	category,
	locationPrimaryText,
	locationSecondaryText,
	links,
	onNameChange,
	onDescriptionChange,
	onCategoryChange,
	onLocationPrimaryTextChange,
	onLocationSelect,
	onLinksChange,
	onSubmit,
	attachmentSlot,
}) => {
	const handleLinkChange = (index: number, value: string) => {
		const newLinks = [...links]
		newLinks[index] = value
		onLinksChange(newLinks)
	}

	const handleAddLink = () => {
		onLinksChange([...links, ''])
	}

	const handleRemoveLink = (index: number) => {
		const newLinks = links.filter((_, i) => i !== index)
		onLinksChange(newLinks)
	}

	return (
		<FormContainer id={formId} onSubmit={onSubmit}>
	<FormGroup>
		<Label htmlFor="idea-location">Location (Optional)</Label>
		<LocationAutocomplete
			id="idea-location"
			value={locationPrimaryText}
			onChange={onLocationPrimaryTextChange}
			onPlaceSelect={onLocationSelect}
			placeholder="Search for a location..."
			autoFocus
		/>
		{locationSecondaryText && (
			<LocationSecondaryText>
				{locationSecondaryText}
			</LocationSecondaryText>
		)}
	</FormGroup>

		<FormGroup>
			<Label htmlFor="idea-name">Name *</Label>
			<Input
				id="idea-name"
				type="text"
				value={name}
				onChange={e => onNameChange(e.target.value)}
				placeholder="Enter idea name"
				autoComplete="off"
				data-1p-ignore
				required
			/>
		</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-category">Category</Label>
				<Select
					id="idea-category"
					aria-label="Category"
					value={category || EVENT_CATEGORIES[0]}
					onChange={e => onCategoryChange(e.target.value as EventCategory)}
					autoComplete="off"
				>
					{EVENT_CATEGORIES.map(cat => (
						<option key={cat} value={cat}>
							{EVENT_CATEGORY_LABELS[cat]}
						</option>
					))}
				</Select>
			</FormGroup>

			<FormGroup>
				<Label>Links</Label>
				{links.length > 0 && (
					<LinksContainer>
						{links.map((link, index) => (
							<LinkInputRow key={index}>
								<LinkInput
									type="text"
									placeholder="Enter a link or any text (e.g., https://example.com)"
									value={link}
									onChange={e => handleLinkChange(index, e.target.value)}
									autoComplete="off"
								/>
								<RemoveLinkButton
									type="button"
									onClick={() => handleRemoveLink(index)}
									title="Remove link"
								>
									<CancelIcon sx={{ fontSize: 18 }} />
								</RemoveLinkButton>
							</LinkInputRow>
						))}
					</LinksContainer>
				)}
				<AddLinkButton
					type="button"
					onClick={handleAddLink}
				>
					+ Add Link
				</AddLinkButton>
			</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-description">Description</Label>
				<TextArea
					id="idea-description"
					value={description}
					onChange={e => onDescriptionChange(e.target.value)}
					placeholder="Add any notes or details about this idea"
					rows={4}
					autoComplete="off"
				/>
			</FormGroup>

			{attachmentSlot}
		</FormContainer>
	)
}

export default IdeaForm
